'use client'

import { useState, useSyncExternalStore } from 'react'
import { Clock, X } from 'lucide-react'
import { useLanguage } from '@/lib/i18n/LanguageContext'
import {
  CLOCK_SKEW_NOTICE_THRESHOLD_MS,
  getClockOffsetMs,
  subscribeClockOffset,
} from '@/lib/time/serverClock'

const DISMISS_KEY = 'confy:clock-skew-notice-dismissed'

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    // SSR or storage blocked – show the notice.
    return false
  }
}

/**
 * Tells the user their device clock is wrong. The app itself copes (auth and
 * deadlines run on serverNow()), but other sites, payment pages and email
 * links may not, and the fix is a single device setting.
 *
 * The offset lives on globalThis in the browser and is 0 on the server; the
 * server snapshot keeps hydration consistent, then the real offset renders.
 */
export default function ClockSkewNotice() {
  const { t } = useLanguage()
  const offsetMs = useSyncExternalStore(subscribeClockOffset, getClockOffsetMs, () => 0)
  const [dismissed, setDismissed] = useState(readDismissed)

  if (dismissed || Math.abs(offsetMs) < CLOCK_SKEW_NOTICE_THRESHOLD_MS) return null

  const totalMinutes = Math.round(Math.abs(offsetMs) / 60_000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  const amount =
    hours === 0
      ? t('authModal.clockSkewMinutes', { minutes: String(minutes) })
      : minutes === 0
        ? t('authModal.clockSkewHoursOnly', { hours: String(hours) })
        : t('authModal.clockSkewHours', { hours: String(hours), minutes: String(minutes) })

  const dismiss = () => {
    setDismissed(true)
    try {
      sessionStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // Ignore – dismissal lasts for this page view only.
    }
  }

  return (
    <div role="alert" className="bg-amber-50 border-b border-amber-200 text-amber-900">
      <div className="mx-auto max-w-7xl px-4 py-2.5 flex items-start gap-3 text-sm">
        <Clock className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
        <p className="flex-1 min-w-0">
          {t('authModal.clockSkewNotice', { amount })}{' '}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="font-medium underline underline-offset-2 hover:text-amber-950"
          >
            {t('authModal.clockSkewReload')}
          </button>
        </p>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('authModal.clockSkewDismiss')}
          className="shrink-0 rounded p-0.5 hover:bg-amber-100"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
